import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrders } from '../services/api';
import './MyOrders.css';

/* ═══ SVG Icons ═══ */
const base = (s, w) => ({
  width: s, height: s, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: w,
  strokeLinecap: 'round', strokeLinejoin: 'round',
});

const IcBox = ({ size = 24, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <path d="M12 3 20 7.5v9L12 21 4 16.5v-9Z" />
    <path d="M4 7.5 12 12l8-4.5M12 12v9" />
  </svg>
);

const IcArrow = ({ size = 16, sw = 2 }) => (
  <svg {...base(size, sw)}><path d="M5 12h14M12 5l7 7-7 7" /></svg>
);

const IcClock = ({ size = 14, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IcReturn = ({ size = 14, sw = 1.8 }) => (
  <svg {...base(size, sw)}>
    <path d="M3.2 12a8.8 8.8 0 1 0 2.85-6.5" />
    <path d="M3 4.6v4.6h4.6" />
  </svg>
);

/* ─── Status Configuration ─── */
const STATUS_CONFIG = {
  pending:    { label: 'Pending',   color: 'yellow',  icon: '⏳' },
  confirmed:  { label: 'Confirmed', color: 'blue',    icon: '✓' },
  shipped:    { label: 'Shipped',   color: 'purple',  icon: '🚚' },
  delivered:  { label: 'Delivered', color: 'green',   icon: '✓' },
  cancelled:  { label: 'Cancelled', color: 'red',     icon: '✕' },
};

export default function MyOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/orders' } } });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getOrders();
        if (!cancelled) setOrders(res.data || []);
      } catch (e) {
        if (!cancelled) setError('Unable to load orders.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, navigate]);

  const filtered = filter === 'all'
    ? orders
    : orders.filter((o) => o.status === filter);

  const formatPrice = (v) =>
    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v ?? 0);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });

  if (!user) return null;

  return (
    <div className="mo-page">
      <div className="mo-container">

        {/* Header */}
        <div className="mo-header">
          <div>
            <h1 className="mo-title">My Orders</h1>
            <p className="mo-subtitle">
              {orders.length} {orders.length === 1 ? 'order' : 'orders'} total
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mo-filters">
          {['all', 'pending', 'confirmed', 'shipped', 'delivered', 'cancelled'].map((f) => (
            <button
              key={f}
              className={`mo-filter ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : STATUS_CONFIG[f]?.label || f}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="mo-loading">
            <div className="mo-spinner" />
            <p>Loading orders…</p>
          </div>
        ) : error ? (
          <div className="mo-error">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="mo-empty">
            <div className="mo-empty-icon"><IcBox size={48} sw={1.3} /></div>
            <h2>No orders yet</h2>
            <p>You have not placed any orders yet.</p>
            <Link to="/shop" className="mo-cta">
              Start Shopping <IcArrow size={15} />
            </Link>
          </div>
        ) : (
          <div className="mo-list">
            {filtered.map((order) => {
              const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              return (
                <Link
                  key={order.id}
                  to={`/orders/${order.order_number}`}
                  className="mo-card"
                >
                  {/* Top row */}
                  <div className="mo-card-top">
                    <div>
                      <div className="mo-order-number">#{order.order_number}</div>
                      <div className="mo-order-date">
                        <IcClock size={13} />
                        {formatDate(order.created_at)}
                      </div>
                    </div>
                    <span className={`mo-status mo-status-${cfg.color}`}>
                      {cfg.label}
                    </span>
                  </div>

                  {/* Image + count */}
                  <div className="mo-card-middle">
                    {order.first_item_image ? (
                      <img
                        src={order.first_item_image}
                        alt="Order item"
                        className="mo-thumb"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="mo-thumb mo-thumb-placeholder">
                        <IcBox size={28} />
                      </div>
                    )}
                    <div className="mo-items-info">
                      <div className="mo-items-count">
                        {order.items_count} {order.items_count === 1 ? 'item' : 'items'}
                      </div>
                      <div className="mo-total">₹{formatPrice(order.total_amount)}</div>
                    </div>
                  </div>

                  {/* Bottom — quick actions */}
                  <div className="mo-card-bottom">
                    {order.can_return && (
                      <span className="mo-action-badge mo-action-return">
                        <IcReturn size={13} /> Return available
                      </span>
                    )}
                    {order.can_cancel && (
                      <span className="mo-action-badge mo-action-cancel">
                        Cancel allowed
                      </span>
                    )}
                    <span className="mo-view-link">
                      View details <IcArrow size={13} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}